import { defineStore, storeToRefs } from "pinia";
import { ref, watch } from "vue";
import algoliasearch from "algoliasearch";
import { useUserStore } from "./userStore";
import { useGlobalFilterStore } from "./globalFilterStore";
import algoliaSearchInsights from "search-insights";
import axios from "axios";
import { getEnv } from "@/env";

const algoliaApp = getEnv('VITE_ALGOLIA_APP_ID');
const algoliaSearchKey = getEnv('VITE_ALGOLIA_API_KEY');
const algoliaIndex = "courses";

const isAdminRoute = () => {
    return window.location.pathname === '/admin'
        || window.location.pathname.startsWith('/admin/')
        || window.location.pathname === '/admin-portal'
        || window.location.pathname.startsWith('/admin-portal/');
};

export const useSearchStore = defineStore("search", () => {
    const userStore = useUserStore();
    const { user } = storeToRefs(userStore);
    const globalFilterStore = useGlobalFilterStore();

    const isOpen = ref(false);
    const searchQuery = ref("");
    const searchResults = ref<any[]>([]);
    const searchClient = ref<any>(null);
    const index = ref<any>(null);
    const currentQueryId = ref<string | null>(null);
    const isInitialized = ref(false);

    const itemRefMap = ref(new Map());
    const selectedListItem = ref<any | null>(null);

    // Initialize Algolia with secured key
    const initializeAlgolia = async () => {
        try {
            const { data } = await axios.get('/api/search-key', {
                params: {
                    adminPortal: isAdminRoute(),
                },
            });
            searchClient.value = algoliasearch(algoliaApp, data.searchKey);
            index.value = searchClient.value.initIndex(algoliaIndex);

            // Use the public search key for insights, not the secured key
            algoliaSearchInsights("init", {
                appId: algoliaApp,
                apiKey: algoliaSearchKey, // Changed from data.searchKey
            });
            algoliaSearchInsights("setUserToken", user.value?.user?.uuid ?? "unknown");

            isInitialized.value = true;
        } catch (error) {
            console.error("Failed to initialize Algolia:", error);
        }
    };

    // Initialize when user is available
    userStore.init().then(async () => {
        await initializeAlgolia();
    });

    const open = () => {
        isOpen.value = true;
    };

    const close = () => {
        isOpen.value = false;
        searchQuery.value = "";
        setTimeout(() => {
            document.getElementById("searchButton")?.focus();
        }, 250);
    };

    const goToResult = (path, index, objectID) => {
        if (currentQueryId.value === null) {
            return;
        }
        const payload = {
            userToken: user.value?.user?.uuid ?? "unknown",
            eventName: "search-result_clicked",
            index: algoliaIndex,
            queryID: currentQueryId.value,
            objectIDs: [objectID],
            positions: [index + 1],
        };
        algoliaSearchInsights("clickedObjectIDsAfterSearch", payload);
        window.location.href = path;
    };

    const goToSelectedResult = () => {
        const result = getResultAtIndex(selectedListItem.value);
        if (result) {
            goToResult(
                `/learn/courses/${result.fullSlug}`,
                selectedListItem.value,
                result.objectID
            );
        }
    };

    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    const search = debounce((query) => {
        if (!isInitialized.value || !index.value) {
            console.warn("Algolia not initialized yet");
            return;
        }

        const options: any = {
            hitsPerPage: 10,
            attributesToSnippet: ["description:20"],
            snippetEllipsisText: "...",
            clickAnalytics: true,
        };

        if (globalFilterStore.value && !isAdminRoute()) {
            options.filters = `categories:"${globalFilterStore.value}"`;
        }

        index.value.search(query, options).then(({ hits, queryID }) => {
            unselectAtIndex(selectedListItem.value);
            currentQueryId.value = queryID ?? "";
            selectedListItem.value = 0;
            searchResults.value = hits;
            if (searchResults.value.length > 0) {
                try {
                    setTimeout(() => {
                        selectAtIndex(0);
                    }, 50);
                } catch (e) {}
            }
        });
    }, 500);

    watch(searchQuery, (query) => {
        if (query.length > 2) {
            search(query);
        }
        if (query.length === 0) {
            searchResults.value = [];
        }
    });

    // Re-run the active search when the global filter changes so results
    // immediately reflect the new modality without the user retyping.
    watch(
        () => globalFilterStore.value,
        () => {
            if (searchQuery.value.length > 2) {
                search(searchQuery.value);
            }
        }
    );

    const addListItemRef = (key, methods) => {
        itemRefMap.value.set(key, methods);
    };

    const removeListItemRef = (key) => {
        itemRefMap.value.delete(key);
    };

    const selectAtIndex = (index) => {
        try {
            const item = searchResults.value[index];
            if (item) {
                itemRefMap.value?.get(item.objectID).select();
            }
        } catch (e) {}
    };

    const unselectAtIndex = (index) => {
        try {
            const item = searchResults.value[index];
            if (item) {
                itemRefMap.value?.get(item.objectID).unselect();
            }
        } catch (e) {}
    };

    const getResultAtIndex = (index) => {
        return searchResults.value[index];
    };

    const selectListItem = (direction) => {
        unselectAtIndex(selectedListItem.value);

        if (selectedListItem.value === null) {
            selectedListItem.value = 0;
        } else {
            selectedListItem.value += direction;
        }
        if (selectedListItem.value < 0) {
            selectedListItem.value = searchResults.value.length - 1;
        } else if (selectedListItem.value >= searchResults.value.length) {
            selectedListItem.value = 0;
        }

        selectAtIndex(selectedListItem.value);
    };

    const selectNextListItem = () => {
        selectListItem(1);
    };

    const selectPreviousListItem = () => {
        selectListItem(-1);
    };

    const clearCurrentSelection = () => {
        unselectAtIndex(selectedListItem.value);
        selectedListItem.value = null;
    };

    return {
        isOpen,
        searchQuery,
        searchResults,
        isInitialized,

        open,
        close,
        addListItemRef,

        selectNextListItem,
        selectPreviousListItem,
        removeListItemRef,
        clearCurrentSelection,
        goToSelectedResult,
        goToResult,
    };
});
